import { inject, Injectable } from '@angular/core';
import { Table as DexieTable } from 'dexie';
import { AppDB } from './dexieDB';
import { SBAGP_Item } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-sbagp-modal/baseitems-sbagp-modal.component';
import { Table } from 'src/app/models/ui/table';
import { SWTYP_Item } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-swtyp-modal/baseitems-swtyp-modal.component';
import { SBFEK_Item } from 'src/app/content/_components/_overlays/gleis-code-verwaltung/gleis-code-verwaltung.component';
import {
  SSCHF_Item,
  SSCHF_Typ,
} from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-sschf-modal/baseitems-sschf-modal.component';
import { SGATT_Item } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-sgatt-modal/baseitems-sgatt-modal.component';
import { SBAUW_Item } from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-sbauw-modal/baseitems-sbauw-modal.component';
import {
  assertBaseDataTable,
  BaseDataTable,
  BaseItem,
} from 'src/app/content/_components/_modals/baseitems-wrapper/baseitems-wrapper.component';
import { DBStatus } from 'src/app/types/api';
import { AsMappedTableFE, GetDBStatus } from './sharedDB';
import { HilfeBeschreibung } from 'src/app/content/_components/_modals/hilfebeschreibungmodal/hilfebeschreibungmodal.component';
import { max } from 'src/app/misc/functions';
import { Key } from 'src/app/types/shared';

@Injectable({ providedIn: 'root' })
export class BaseItemsDB {
  private readonly db = inject(AppDB);

  async GetBaseItems(
    tabelle: string,
    sub_route: number | string
  ): Promise<
    string[] | SBAGP_Item[] | Table<SWTYP_Item | SBFEK_Item | SSCHF_Item | SGATT_Item | SBAUW_Item | BaseItem>
  > {
    const tableName = assertBaseTableDB(tabelle);
    if (isBaseItemTable(tableName)) return this.getBaseItemsFromTabelle(tableName);
    if (isBaseItemTypTable(tableName)) return this.getBaseItemsByOTYPID(tableName, sub_route as number);

    switch (tableName) {
      case 'kspezw':
        return this.getBaseItemsFromKSPEZW(sub_route as number);
      case 'sschf':
        return this.getBaseItemsFromSSCHF();
      case 'swtyp':
        return this.getBaseItemsFromSWTYP();
      case 'sbfek':
        return this.getBaseItemsFromSBFEK();
      case 'sbagp':
        return this.getBaseItemsFromSBAGP(sub_route as string);
      case 'sbauw':
        return this.getBaseItemsFromSBAUW();
      case 'sgatt':
        return this.getBaseItemsFromSGATT();
      case 'sstamm':
        return this.getBaseItemsFromSSTAMM(sub_route as number);
    }
  }

  async SetBaseItems(
    tabelle: string,
    sub_route: number | string,
    item: SBAGP_Item | SWTYP_Item | SBFEK_Item | SSCHF_Item | SGATT_Item | SBAUW_Item | BaseItem
  ): Promise<number> {
    const tableName = assertBaseTableDB(tabelle);
    if (isBaseItemTable(tableName)) return this.setBaseItemsInTabelle(tableName, item as BaseItem);
    if (isBaseItemTypTable(tableName))
      return this.setBaseItemWithOTYPID(tableName, sub_route as number, item as BaseItem);

    switch (tableName) {
      case 'kspezw':
        return this.setBaseItemsInKSPEZW(sub_route as number, item as BaseItem);
      case 'sschf':
        return this.setBaseItemsInSSCHF(item as SSCHF_Item);
      case 'swtyp':
        return this.setBaseItemsInSWTYP(item as SWTYP_Item);
      case 'sbfek':
        return this.setBaseItemsInSBFEK(item as SBFEK_Item);
      case 'sbagp':
        throw new Error('SBAGP soll über setBaugruppe gespeichert werden');
      case 'sbauw':
        return this.setBaseItemsInSBAUW(item as SBAUW_Item);
      case 'sgatt':
        return this.setBaseItemsInSGATT(item as SGATT_Item);
      case 'sstamm':
        return this.setBaseItemsInSSTAMM(sub_route as number, item as BaseItem);
    }
  }

  async SetBaugruppe(Name: string, items: SBAGP_Item[]): Promise<void> {
    await this.db.transaction('rw', [this.db.sbagp], async () => {
      for (const item of items) {
        const { ID, Pruefart, Anzahl } = item;
        const sbagp: SBAGP = { Name, Pruefart, Anzahl, ...GetDBStatus() };
        if (ID) {
          await this.db.sbagp.update(ID, sbagp);
        } else {
          await this.db.sbagp.add(sbagp);
        }
      }
    });
  }

  async RenameBaugruppe(Name: string, newName: string): Promise<void> {
    await this.db.sbagp.where({ Name }).modify({ Name: newName, ...GetDBStatus() });
  }

  async GetSelectionFromTable(tabelle: string, tab_id: number): Promise<string[]> {
    try {
      assertBaseDataTable(tabelle.toUpperCase());
      const tableName = tabelle.toLowerCase() as BaseDataTableDB;

      if (tableName == 'kspezw') {
        const rows = await this.db.kspezw.where({ TABWERT_ID: tab_id }).toArray();
        return [...new Set(rows?.map(x => x.Bezeichnung))];
      }

      if (tableName == 'sbagp') {
        const rows = await this.db.sbagp.orderBy('Name').toArray();
        return [...new Set(rows.map(x => x.Name))];
      } else {
        const rows = await this.db[tableName].orderBy('Bezeichnung').toArray();
        return [...new Set(rows.map(x => x.Bezeichnung))];
      }
    } catch (error) {
      console.error('Fehler beim GetSelectionFromTable: ', error);
      return [];
    }
  }

  async GetHelperBeschreibung(tabelle: string, id: number): Promise<HilfeBeschreibung> {
    const tableName = assertHelperTable(tabelle);
    const { FileTyp, HelpBeschreibung, HelpBildBase64 } = await this.db[tableName].get(id);
    return { FileTyp, HelpBeschreibung, HelpBildBase64 };
  }

  async SetHelperBeschreibung(tabelle: string, id: number, helper: HilfeBeschreibung): Promise<void> {
    const tableName = assertHelperTable(tabelle);
    await this.db[tableName].update(id, { ...helper, ...GetDBStatus() });
  }

  private async getBaseItemsFromTabelle(tabelle: BASE_ITEM_ONLY_TABLE): Promise<Table<BaseItem>> {
    const rows = await this.db[tabelle].orderBy('Bezeichnung').toArray();
    return AsBaseItem(rows);
  }

  private async setBaseItemsInTabelle(tabelle: BASE_ITEM_ONLY_TABLE, item: BaseItem): Promise<number> {
    let { ID, Bezeichnung } = item;
    const base_item: BASE_ITEM = { Bezeichnung, ...GetDBStatus() };
    if (ID) {
      await this.db[tabelle].update(ID, base_item);
    } else {
      ID = await this.db[tabelle].add(base_item);
    }
    return ID;
  }

  private async getBaseItemsByOTYPID(tabelle: BASE_ITEM_OTYPID_ONLY_TABLE, OTYPID: number): Promise<Table<BaseItem>> {
    const rows = await this.db[tabelle].where({ OTYPID }).sortBy('Bezeichnung');
    return AsBaseItem(rows);
  }

  private async setBaseItemWithOTYPID(
    tabelle: BASE_ITEM_OTYPID_ONLY_TABLE,
    OTYPID: number,
    item: BaseItem
  ): Promise<number> {
    let { ID, Bezeichnung } = item;
    const base_item: BASE_ITEM_OTYPID = { OTYPID, Bezeichnung, ...GetDBStatus() };
    if (ID) {
      await this.db[tabelle].update(ID, base_item);
    } else {
      ID = await this.db[tabelle].add(base_item);
    }
    return ID;
  }

  private async getBaseItemsFromKSPEZW(TABWERT_ID: number): Promise<Table<BaseItem>> {
    const rows = await this.db.kspezw.where({ TABWERT_ID }).sortBy('Bezeichnung');
    return AsBaseItem(rows);
  }

  private async setBaseItemsInKSPEZW(TABWERT_ID: number, item: BaseItem): Promise<number> {
    let { ID, Bezeichnung } = item;
    const kspezw: KSPEZW = {
      TABWERT_ID,
      Bezeichnung,
      ...GetDBStatus(),
    };
    if (ID) {
      await this.db.kspezw.update(ID, kspezw);
    } else {
      ID = await this.db.kspezw.add(kspezw);
    }

    return ID;
  }

  private async getBaseItemsFromSSCHF(): Promise<Table<SSCHF_Item>> {
    const rows = await this.db.sschf.orderBy('Bezeichnung').toArray();
    for (const row of rows) {
      if (row.Messebene <= 0) row.Messebene = 10;
    }

    return AsMappedTableFE<SSCHF_Item>()(rows, [
      'ID',
      'Bezeichnung',
      ['Typ', 'Typ', val => (val == 'Vignol' ? SSCHF_Typ.Vignol : SSCHF_Typ.Rille)],
      ['SchHMax', 'Höhe'],
      ['Messebene', 'Messebene'],
      ['BildBase64', 'Skizze'],
      'Profil',
      'UserName',
      'Status',
    ]);
  }

  private async setBaseItemsInSSCHF(item: SSCHF_Item): Promise<number> {
    let { ID, Bezeichnung } = item;
    const sschf: SSCHF = {
      HV_Max: 0,
      Bezeichnung,
      SchHMax: item.Höhe,
      Typ: item.Typ == SSCHF_Typ.Vignol ? 'Vignol' : 'Rille',
      BildBase64: item.Skizze,
      Profil: item.Profil,
      Messebene: item.Messebene,
      ...GetDBStatus(),
    };
    if (ID) {
      await this.db.sschf.update(ID, sschf);
    } else {
      ID = await this.db.sschf.add(sschf);
    }

    return ID;
  }

  private async getBaseItemsFromSWTYP(): Promise<Table<SWTYP_Item>> {
    const rows = await this.db.swtyp.orderBy('Bezeichnung').toArray();
    return AsMappedTableFE<SWTYP_Item>()(rows, ['ID', 'Bezeichnung', ['TypID', 'Typ'], 'UserName', 'Status']);
  }

  private async setBaseItemsInSWTYP(item: SWTYP_Item): Promise<number> {
    let { ID, Bezeichnung, Typ } = item;
    const swtyp: SWTYP = {
      Bezeichnung,
      TypID: Typ,
      ...GetDBStatus(),
    };
    if (ID) {
      await this.db.swtyp.update(ID, swtyp);
    } else {
      ID = await this.db.swtyp.add(swtyp);
    }

    return ID;
  }

  private async getBaseItemsFromSBFEK(): Promise<Table<SBFEK_Item>> {
    const rows = await this.db.sbfek.orderBy('Bezeichnung').toArray();
    return AsMappedTableFE<SBFEK_Item>()(rows, [
      'ID',
      ['Bezeichnung', 'Code'],
      ['Schienenform_ID', 'Schienenform'],
      ['Schwellenart_ID', 'Schwellenart'],
      ['Schienenbefestigung_ID', 'Schienenbefestigung'],
      ['Eindeckung_ID', 'Eindeckung'],
      ['BildBase64', 'Bild'],
      ['EPreis', 'Kosten'],
      'Geschlossen',
      'UserName',
      'Status',
    ]);
  }

  private async setBaseItemsInSBFEK(item: SBFEK_Item): Promise<number> {
    const sbfek: SBFEK = {
      Bezeichnung: item.Code,
      Schienenform_ID: item.Schienenform,
      Schwellenart_ID: item.Schwellenart,
      Schienenbefestigung_ID: item.Schienenbefestigung,
      Eindeckung_ID: item.Eindeckung,
      BildBase64: item.Bild,
      EPreis: item.Kosten,
      Geschlossen: item.Geschlossen,
      ...GetDBStatus(),
    };
    if (item.ID) {
      await this.db.sbfek.update(item.ID, sbfek);
    } else {
      item.ID = await this.db.sbfek.add(sbfek);
    }

    return item.ID;
  }

  private async getBaseItemsFromSBAGP(Name: string): Promise<string[] | SBAGP_Item[]> {
    if (Name == null) {
      const names = await this.db.sbagp.orderBy('Name').toArray();
      return [...new Set(names.map(x => x.Name))];
    }
    if (Name == '0') {
      return _EMPTY_BAUGRUPPE.map<SBAGP_Item>(Pruefart => ({ Pruefart, ID: null, Anzahl: 0 }));
    }

    const rows = await this.db.sbagp.where({ Name }).toArray();
    return rows as SBAGP_Item[];
  }

  private async getBaseItemsFromSBAUW(): Promise<Table<SBAUW_Item>> {
    const rows = await this.db.sbauw.orderBy('Bezeichnung').toArray();
    return AsMappedTableFE<SBAUW_Item>()(rows, [
      'ID',
      'Bezeichnung',
      ['Pruefung', 'Prüfung'],
      ['AnweisungBase64', 'Anweisung'],
      'UserName',
      'Status',
    ]);
  }

  private async setBaseItemsInSBAUW(item: SBAUW_Item): Promise<number> {
    let { ID, Bezeichnung } = item;
    const sbauw: SBAUW = {
      Bezeichnung,
      AnweisungBase64: item.Anweisung,
      Pruefung: item.Prüfung,
      ...GetDBStatus(),
    };
    if (ID) {
      await this.db.sbauw.update(ID, sbauw);
    } else {
      ID = await this.db.sbauw.add(sbauw);
    }

    return ID;
  }

  private async getBaseItemsFromSGATT(): Promise<Table<SGATT_Item>> {
    const rows = await this.db.sgatt.where({ OTYPID: 2 }).sortBy('IntID');
    return AsMappedTableFE<SGATT_Item>()(rows, ['ID', ['IntID', 'GattungId'], 'Bezeichnung', 'UserName', 'Status']);
  }

  private async setBaseItemsInSGATT(item: SGATT_Item): Promise<number> {
    let { ID, Bezeichnung } = item;
    if (ID) {
      const row = await this.db.sgatt.get(ID);
      if (row.IntID <= 10) throw new Error('Bearbeitung ist von System gesperrt');

      await this.db.sgatt.update(ID, { Bezeichnung, ...GetDBStatus() });
    } else {
      const rows = await this.db.sgatt.where({ OTYPID: 2 }).toArray();
      const maxIntID = max(rows, 'IntID');
      const sgatt: SGATT = {
        Bezeichnung,
        IntID: maxIntID + 1,
        ...GetDBStatus(),
      };
      ID = await this.db.sgatt.add(sgatt);
    }

    return ID;
  }

  private async getBaseItemsFromSSTAMM(GattungID: number): Promise<Table<BaseItem>> {
    const rows = await this.db.sstamm.where({ OTYPID: 2, GattungID }).sortBy('Bezeichnung');
    return AsBaseItem(rows);
  }

  private async setBaseItemsInSSTAMM(GattungID: number, item: BaseItem): Promise<number> {
    let { ID, Bezeichnung } = item;
    const sstamm: SSTAMM = {
      GattungID,
      Bezeichnung,
      ...GetDBStatus(),
    };
    if (ID) {
      await this.db.sstamm.update(ID, sstamm);
    } else {
      ID = await this.db.sstamm.add(sstamm);
    }

    return ID;
  }
}
//#region Types & Assertions
export type BaseDataTableDB = Lowercase<Exclude<BaseDataTable, 'KAUFUS'>>;

export type BASE_ITEM_TABLE = {
  [K in BaseDataTableDB]: AppDB[K] extends DexieTable<BASE_ITEM> ? K : never;
}[BaseDataTableDB];

export type BASE_ITEM_OTYPID_TABLE = {
  [K in BASE_ITEM_TABLE]: AppDB[K] extends DexieTable<BASE_ITEM_OTYPID> ? K : never;
}[BASE_ITEM_TABLE];

export type BASE_ITEM_ONLY_TABLE = {
  [K in BASE_ITEM_TABLE]: AppDB[K] extends DexieTable<infer ITEM extends BASE_ITEM> ?
    BASE_ITEM extends ITEM ?
      K
    : never
  : never;
}[BASE_ITEM_TABLE];

export type BASE_ITEM_OTYPID_ONLY_TABLE = {
  [K in BASE_ITEM_OTYPID_TABLE]: AppDB[K] extends DexieTable<infer ITEM extends BASE_ITEM_OTYPID> ?
    BASE_ITEM_OTYPID extends ITEM ?
      K
    : never
  : never;
}[BASE_ITEM_OTYPID_TABLE];

function assertBaseTableDB(tabelle: string) {
  assertBaseDataTable(tabelle);
  if (tabelle == 'KAUFUS') throw new Error('KAUFUS ist nicht unterstützt im Replikat');
  return tabelle.toLowerCase() as BaseDataTableDB;
}

function isBaseItemTable(tabelle: BaseDataTableDB): tabelle is BASE_ITEM_ONLY_TABLE {
  switch (tabelle as BASE_ITEM_ONLY_TABLE) {
    case 'sbeft':
    case 'sschw':
    case 'szubf':
    case 'sradl':
    case 'santr':
    case 'svest':
    case 'srich':
    case 'seind':
    case 'smeh':
    case 'suntg':
      return true;
    default:
      return false;
  }
}

function isBaseItemTypTable(tabelle: BaseDataTableDB): tabelle is BASE_ITEM_OTYPID_ONLY_TABLE {
  switch (tabelle as BASE_ITEM_OTYPID_ONLY_TABLE) {
    case 'kequi':
    case 'sprue':
    case 'smang':
    case 'slage':
    case 'sinst':
      return true;
    default:
      return false;
  }
}

type HILFE_TABLE = {
  [K in Key<AppDB>]: AppDB[K] extends DexieTable<HilfeBeschreibung> ? K : never;
}[Key<AppDB>];

function assertHelperTable(tabelle: string): HILFE_TABLE {
  tabelle = tabelle.toLowerCase();
  switch (tabelle as HILFE_TABLE) {
    case 'smang':
    case 'szaehn':
    case 'kwaart':
      return tabelle as HILFE_TABLE;
    default:
      throw new Error(`Tabelle ist ungültig: ${tabelle}`);
  }
}
//#endregion

//#region Helper Funktions
function AsBaseItem(rows: KSPEZW[] | BASE_ITEM[]): Table<BaseItem> {
  return AsMappedTableFE<BaseItem>()(rows);
}
//#endregion
//#region DB Interfaces
export interface KSPEZW extends BASE_ITEM {
  TABWERT_ID: number;
}

export interface SBAGP extends DBStatus {
  ID?: number;
  Name: string;
  Pruefart: string;
  Anzahl: number;
}

export interface BASE_ITEM extends DBStatus {
  ID?: number;
  Bezeichnung: string;
}

export interface SWTYP extends BASE_ITEM {
  TypID: number;
}

export interface SBFEK extends BASE_ITEM {
  Schienenform_ID: string /* Bezeichnung */;
  Schwellenart_ID: string /* Bezeichnung */;
  Schienenbefestigung_ID: string /* Bezeichnung */;
  Eindeckung_ID: string /* Bezeichnung */;
  BildBase64: string;
  EPreis: number;
  Geschlossen: boolean;
  Sortierung?: number;
}
export interface SSCHF extends BASE_ITEM {
  Typ: string;
  HV_Max: number;
  SchHMax: number;
  Messebene: number;
  BildBase64: string;
  Profil: string;
}

export interface SBAUW extends BASE_ITEM {
  Pruefung: boolean;
  AnweisungBase64: string;
}

export interface SSTAMM extends BASE_ITEM {
  GattungID: number;
}

export interface SGATT extends BASE_ITEM {
  IntID: number;
}

export interface SMANG extends BASE_ITEM_OTYPID, HilfeBeschreibung {
  // SMATYID?: number;
  // InCheckListe?: boolean;
  // PosCheckListe?: number;
}

export interface BASE_ITEM_OTYPID extends BASE_ITEM {
  OTYPID: number;
}
//#endregion

const _EMPTY_BAUGRUPPE = [
  'Federringe',
  'Gleitstuehle/-platten',
  'Hakenschrauben',
  'Klemmplatten',
  'Schwellen',
  'Schwellenschrauben',
  'Spannklemmen',
  'Zwischenlagen',
];
